import { combineReducers } from '@reduxjs/toolkit';
import { persistReducer, createMigrate, createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { magicNumbers, getEnv } from 'utils';
import {
  filterSlice,
  kioskSlice,
  tagSlice,
  resourceSlice,
  resourceDetailSlice,
  pageSlice,
  settingsSlice,
  newsContentSlice,
  copyNewsContentSlice,
  newsSearchResourceSlice,
  instagramContentSlice,
  instagramCommentByCommentContentSlice,
  instagramCommentByPostContentSlice,
  instagramSearchResourceSlice,
  twitterContentSlice,
  twitterSearchResourceSlice,
  virastyContentSlice,
  virastySearchResourceSlice,
  telegramContentSlice,
  linkedinContentSlice,
  telegramSearchResourceSlice,
  newspaperContentSlice,
  newspaperSearchResourceSlice,
  resourceCategorySlice,
  vipResourcesSlice,
  subjectSlice,
  comparisonSlice,
  onboardingSlice,
  alarmSlice,
  errorSlice,
  shepherdSlice,
  bulletinSettingsSlice,
  bulletinContentSlice,
  bulletinExportSettingsSlice,
  blackListSlice,
  notificationSlice,
  contentSlice,
  excelExportSlice,
  userSlice,
  authSlice,
  accountSlice,
  channelSlice,
  supportSlice,
  chartSlice,
  contentDetailSlice,
  autoBulletinSlice,
  multiPlatformContentSlice,
  storySlice,
  nativePlatformsContentSlice,
  nativePlatformsSearchResourceSlice,
  bulletinSlice,
  bulletinContentQuerySlice,
  tinyUrlSlice,
  companySlice,
  departmentSlice,
  dashboardSlice,
  statisticsSlice,
  brandBaanSlice,
  brandBuilderSlice,
  feedbackSlice,
  aiSummarizerSlice,
  ecommerceSlice,
  bulletinPdfDraftSlice,
  netSentimentScoreSlice,
} from './index';
import {
  DEFAULT_ECOMMERCE_PERIOD,
  ECOMMERCE_PERIODS,
} from './ecommerce/ecommerce.helpers';
import {
  migrations,
  accountMigrations,
  contentMigrations,
  authMigrations,
} from './migrations';

const rootPersistConfig = {
  key: getEnv('REACT_APP_NAME'),
  keyPrefix: localStorage.getItem('keyPrefix') || 'root:',
  version: magicNumbers.REDUX_PERSIST_VERSION_NUMBER,
  storage,
  whitelist: [
    'onboardingReducer',
    'multiPlatformContentReducer',
    'newsContentReducer',
    'instagramContentReducer',
    'instagramCommentByCommentContentReducer',
    'instagramCommentByPostContentReducer',
    'twitterContentReducer',
    'virastyContentReducer',
    'telegramContentReducer',
    'linkedinContentReducer',
    'newspaperContentReducer',
    'nativePlatformsContentReducer',
    'newsSearchResourceReducer',
    'instagramSearchResourceReducer',
    'twitterSearchResourceReducer',
    'virastySearchResourceReducer',
    'telegramSearchResourceReducer',
    'newspaperSearchResourceReducer',
    'nativePlatformsSearchResourceReducer',
    'filterReducer',
    'kioskReducer',
    'settingsReducer',
    'pageReducer',
    'notificationReducer',
    'shepherdReducer',
    'vipResourcesReducer',
    'chartReducer',
    'errorReducer',
  ],
  migrate: createMigrate(migrations, {
    debug: getEnv('REACT_APP_ENVIRONMENT') === 'development',
  }),
  timeout: null,
};

const accountPersistConfig = {
  key: 'account',
  keyPrefix: 'root:',
  storage,
  version: magicNumbers.REDUX_PERSIST_ACCOUNT_SLICE_VERSION,
  whitelist: ['currentUser', 'hijackerUser'],
  migrate: createMigrate(accountMigrations, {
    debug: getEnv('REACT_APP_ENVIRONMENT') === 'development',
  }),
};

const authPersistConfig = {
  key: 'auth',
  keyPrefix: 'root:',
  storage,
  version: magicNumbers.REDUX_PERSIST_VERSION_NUMBER,
  whitelist: ['isLoggedIn'],
  migrate: createMigrate(authMigrations, {
    debug: getEnv('REACT_APP_ENVIRONMENT') === 'development',
  }),
};

const contentPersistConfig = {
  key: 'content',
  keyPrefix: 'root:',
  storage,
  version: magicNumbers.REDUX_PERSIST_VERSION_NUMBER,
  whitelist: ['resultSearchQuery', 'contentAISummary'],
  migrate: createMigrate(contentMigrations, {
    debug: getEnv('REACT_APP_ENVIRONMENT') === 'development',
  }),
};

/**
 * Only the dashboard period is kept, so the last chosen range survives a
 * refresh while the fetched widget data is always loaded fresh.
 */
const ecommercePersistConfig = {
  key: 'ecommerce',
  keyPrefix: 'root:',
  storage,
  version: magicNumbers.REDUX_PERSIST_VERSION_NUMBER,
  whitelist: ['period'],
  /**
   * A saved period that the dashboard no longer offers (such as the removed
   * hourly ones) falls back to the default instead of being requested.
   */
  transforms: [
    createTransform(
      (period) => period,
      (period) =>
        ECOMMERCE_PERIODS.includes(period) ? period : DEFAULT_ECOMMERCE_PERIOD,
      { whitelist: ['period'] }
    ),
  ],
};

const appReducer = combineReducers({
  contentDetailReducer: contentDetailSlice.reducer,
  excelExportReducer: excelExportSlice.reducer,
  multiPlatformContentReducer: multiPlatformContentSlice.reducer,
  newsContentReducer: newsContentSlice.reducer,
  copyNewsContentReducer: copyNewsContentSlice.reducer,
  instagramContentReducer: instagramContentSlice.reducer,
  instagramCommentByCommentContentReducer:
    instagramCommentByCommentContentSlice.reducer,
  instagramCommentByPostContentReducer:
    instagramCommentByPostContentSlice.reducer,
  twitterContentReducer: twitterContentSlice.reducer,
  virastyContentReducer: virastyContentSlice.reducer,
  telegramContentReducer: telegramContentSlice.reducer,
  linkedinContentReducer: linkedinContentSlice.reducer,
  nativePlatformsContentReducer: nativePlatformsContentSlice.reducer,
  newspaperContentReducer: newspaperContentSlice.reducer,
  newsSearchResourceReducer: newsSearchResourceSlice.reducer,
  instagramSearchResourceReducer: instagramSearchResourceSlice.reducer,
  twitterSearchResourceReducer: twitterSearchResourceSlice.reducer,
  virastySearchResourceReducer: virastySearchResourceSlice.reducer,
  telegramSearchResourceReducer: telegramSearchResourceSlice.reducer,
  newspaperSearchResourceReducer: newspaperSearchResourceSlice.reducer,
  nativePlatformsSearchResourceReducer:
    nativePlatformsSearchResourceSlice.reducer,
  pageReducer: pageSlice.reducer,
  kioskReducer: kioskSlice.reducer,
  filterReducer: filterSlice.reducer,
  tagReducer: tagSlice.reducer,
  resourceReducer: resourceSlice.reducer,
  resourceDetailReducer: resourceDetailSlice.reducer,
  settingsReducer: settingsSlice.reducer,
  resourceCategoryReducer: resourceCategorySlice.reducer,
  vipResourcesReducer: vipResourcesSlice.reducer,
  subjectReducer: subjectSlice.reducer,
  comparisonReducer: comparisonSlice.reducer,
  onboardingReducer: onboardingSlice.reducer,
  alarmReducer: alarmSlice.reducer,
  errorReducer: errorSlice.reducer,
  shepherdReducer: shepherdSlice.reducer,
  bulletinSettingsReducer: bulletinSettingsSlice.reducer,
  bulletinContentReducer: bulletinContentSlice.reducer,
  bulletinExportSettingsReducer: bulletinExportSettingsSlice.reducer,
  blackListReducer: blackListSlice.reducer,
  autoBulletinReducer: autoBulletinSlice.reducer,
  channelReducer: channelSlice.reducer,
  notificationReducer: notificationSlice.reducer,
  supportReducer: supportSlice.reducer,
  chartReducer: chartSlice.reducer,
  storyReducer: storySlice.reducer,
  bulletinReducer: bulletinSlice.reducer,
  bulletinContentQueryReducer: bulletinContentQuerySlice.reducer,
  userReducer: userSlice.reducer,
  authReducer: persistReducer(authPersistConfig, authSlice.reducer),
  accountReducer: persistReducer(accountPersistConfig, accountSlice.reducer),
  contentReducer: persistReducer(contentPersistConfig, contentSlice.reducer),
  tinyUrlReducer: tinyUrlSlice.reducer,
  companyReducer: companySlice.reducer,
  departmentReducer: departmentSlice.reducer,
  dashboardReducer: dashboardSlice.reducer,
  statisticsReducer: statisticsSlice.reducer,
  brandBaanReducer: brandBaanSlice.reducer,
  brandBuilderReducer: brandBuilderSlice.reducer,
  feedbackReducer: feedbackSlice.reducer,
  aiSummarizerReducer: aiSummarizerSlice.reducer,
  ecommerceReducer: persistReducer(
    ecommercePersistConfig,
    ecommerceSlice.reducer
  ),
  bulletinPdfDraftReducer: bulletinPdfDraftSlice.reducer,
  netSentimentScoreReducer: netSentimentScoreSlice.reducer,
});

const rootReducer = (state, action) => {
  if (action.type === 'USER_LOGOUT') {
    storage.removeItem(`hijack:${getEnv('REACT_APP_NAME')}`);
    storage.removeItem('root:content');
    storage.removeItem('root:account');
    storage.removeItem('root:auth');
    localStorage.clear();
    sessionStorage.clear();
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

export const persistedRootReducer = persistReducer(
  rootPersistConfig,
  rootReducer
);
